import { HttpClient } from '@euler/lib/request';
import {
  createSSEConnection,
  SSEConnection,
} from '@euler/lib/request/SSEClient';
import { TokenService } from '@euler/lib/services';
import {
  AIAskInput,
  AIMessagePage,
  AIPageContext,
  AISession,
  AISuggestion,
  kAiClientFeatures,
} from '@euler/model/ai';

import {
  AiHomeActivityResult,
  AiHomeSessionsCursor,
  AiHomeSessionsResult,
  AiHomeSuggestionsResult,
} from '@euler/model/ai-home';

export type AIAskCallbacks = {
  /** A new conversation's id, sent before its first reply. */
  onSession?: (sessionId: string) => void;
  onToken: (text: string) => void;
  /** `progress` is the tool's own status line, when it has one. */
  onToolCall?: (name: string, args: any, progress?: string) => void;
  onToolResult?: (name: string, result: any) => void;
  onDone: (
    messageId: string,
    sessionId: string,
    usage: { inputTokens: number; outputTokens: number },
  ) => void;
  onError: (message: string, retryAfter?: number) => void;
};

export type TokenRefresher = () => Promise<string | null>;

export class AIAssistantService {
  constructor(
    private readonly api: HttpClient,
    private readonly tokenService: TokenService,
    private readonly endpoint: string,
    private readonly refreshToken?: TokenRefresher,
  ) {}

  /**
   * The session for the screen we are on — one per subject, so a question
   * about one car never replays alongside answers about another.
   *
   * Deliberately NOT `/sessions/current`: that route is frozen for builds
   * that cannot be updated, and still answers with the single legacy
   * session regardless of page.
   */
  async getSessionForPage(
    sessionType: string,
    pageContext?: AIPageContext,
  ): Promise<AISession | null> {
    try {
      const result = await this.api
        .get()
        .url('/sessions/for-page', {
          sessionType,
          page: pageContext?.page,
          params: pageContext
            ? JSON.stringify(pageContext.params ?? {})
            : undefined,
        })
        .future();
      return result as AISession;
    } catch {
      return null;
    }
  }

  async getMessages(
    sessionId: string,
    opts?: { limit?: number; beforeId?: string },
  ): Promise<AIMessagePage> {
    return await this.api
      .get()
      .url('/sessions/:sessionId/messages', {
        sessionId,
        limit: opts?.limit,
        beforeId: opts?.beforeId,
      })
      .future();
  }

  async clearSession(sessionId: string): Promise<void> {
    await this.api
      .post()
      .url('/sessions/:sessionId/clear', { sessionId })
      .future();
  }

  async toggleReaction(messageId: string, reaction: string): Promise<void> {
    await this.api
      .post()
      .url('/messages/:messageId/reaction', { messageId })
      .data({ reaction })
      .future();
  }

  async getSuggestions(
    sessionType: string,
    pageContext?: AIPageContext,
    role?: string,
  ): Promise<AISuggestion[]> {
    return await this.api
      .get()
      .url('/suggestions', {
        sessionType,
        page: pageContext?.page,
        role,
      })
      .future();
  }

  async listHomeSessions(input: {
    keyword: string;
    before: AiHomeSessionsCursor | null;
    limit?: number;
  }): Promise<AiHomeSessionsResult> {
    return await this.api
      .get()
      .url('/home/sessions', {
        limit: input.limit ?? 30,
        ...(input.keyword ? { keyword: input.keyword } : {}),
        ...(input.before
          ? {
              beforeUpdatedAt: input.before.updatedAt,
              beforeId: input.before.id,
            }
          : {}),
      })
      .future();
  }

  /** Without a limit the server picks how many recent items to send. */
  async getHomeActivity(limit?: number): Promise<AiHomeActivityResult> {
    return await this.api
      .get()
      .url('/home/activity', limit ? { limit } : undefined)
      .future();
  }

  /** The caller's conversation that launched a campaign, if any. */
  async getCampaignSession(
    campaignId: string,
  ): Promise<{ sessionId: string | null }> {
    return await this.api
      .get()
      .url('/home/campaign-session', { campaignId })
      .future();
  }

  async getHomeSuggestions(): Promise<AiHomeSuggestionsResult> {
    // Actions the build cannot show (a coaching setup card) are left out.
    return await this.api
      .get()
      .url('/home/suggestions', { clientFeatures: kAiClientFeatures.join(',') })
      .future();
  }

  ask(params: AIAskInput, callbacks: AIAskCallbacks): SSEConnection {
    let connection: SSEConnection | null = null;
    let aborted = false;
    let terminal = false;
    let refreshed = false;

    const connect = (token: string) => {
      if (aborted) return;

      connection = createSSEConnection({
        url: `${this.endpoint}/api/ai/ask`,
        method: 'POST',
        headers: {
          Authorization: token.startsWith('Bearer ')
            ? token
            : `Bearer ${token}`,
        },
        body: JSON.stringify(params),
        onEvent: event => {
          if (aborted || terminal) return;
          try {
            const data = JSON.parse(event.data);
            switch (event.event) {
              case 'session':
                if (typeof data.sessionId === 'string')
                  callbacks.onSession?.(data.sessionId);
                break;
              case 'token':
                callbacks.onToken(data.text);
                break;
              case 'tool_call':
                callbacks.onToolCall?.(
                  data.name,
                  data.args,
                  typeof data.progress === 'string' ? data.progress : undefined,
                );
                break;
              case 'tool_result':
                callbacks.onToolResult?.(data.name, data.result);
                break;
              case 'done':
                terminal = true;
                // The server decides which thread the turn joined, so it
                // is the authority on the session id, not the client.
                callbacks.onDone(data.messageId, data.sessionId, data.usage);
                break;
              case 'error':
                terminal = true;
                callbacks.onError(
                  data.message,
                  data.retryAfterMs ?? data.retryAfter,
                );
                break;
            }
          } catch (e: any) {
            terminal = true;
            callbacks.onError(e.message ?? 'Failed to parse event');
          }
        },
        onComplete: () => {
          if (!aborted && !terminal) {
            terminal = true;
            callbacks.onError('回复意外中断，请重新打开对话核对结果');
          }
        },
        onError: error => {
          if (aborted || terminal) return;
          if (
            (error as any).status === 401 &&
            this.refreshToken != null &&
            !refreshed
          ) {
            refreshed = true;
            void this.refreshToken()
              .then(newToken => {
                if (aborted) return;
                if (newToken) {
                  connect(newToken);
                } else {
                  callbacks.onError('登录已过期，请重新登录');
                }
              })
              .catch(() => {
                if (!aborted) callbacks.onError('登录状态更新失败，请重新登录');
              });
            return;
          }
          terminal = true;
          if (!aborted) callbacks.onError(error.message);
        },
      });
    };

    void this.tokenService
      .getToken()
      .then(token => {
        connect(token ?? '');
      })
      .catch(() => {
        if (!aborted) callbacks.onError('无法读取登录状态，请重新登录');
      });

    return {
      abort: () => {
        aborted = true;
        connection?.abort();
      },
    };
  }
}
